import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  getCurrentSession,
  redirectToFederatedSignIn,
  signOut as cognitoSignOut,
} from "../services/cognitoAuthService";
import { getLogoutRedirectUri, COGNITO_CONFIG } from "../config/cognitoConfig";

const AuthContext = createContext(null);

/**
 * Builds the same `user` object shape Auth0 provided (sub, email, name,
 * email_verified, picture), plus roles derived from Cognito's
 * "cognito:groups" claim — the direct replacement for Auth0's custom
 * "https://api.nephrotrax.com/roles" claim.
 */
function buildUserFromClaims(claims) {
  if (!claims) return null;

  // Cognito only gives a single combined "name" claim (that's all we
  // mapped from Apple/Google during identity provider setup) —
  // some existing code expects Auth0-style separate given_name/family_name
  // fields (e.g. PatientDashboard's profile auto-initialization for new
  // social-login users). Split name as a reasonable approximation rather
  // than changing those call sites.
  const nameParts = (claims.name || "").trim().split(/\s+/);
  const given_name = nameParts[0] || undefined;
  const family_name = nameParts.length > 1 ? nameParts.slice(1).join(" ") : undefined;

  return {
    sub: claims.sub,
    email: claims.email,
    email_verified: claims.email_verified,
    name: claims.name,
    given_name,
    family_name,
    picture: claims.picture, // present for Google-federated users, absent otherwise
    roles: claims["cognito:groups"] || [],
  };
}

export function AuthProvider({ children }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  // Cache the in-flight session check so multiple simultaneous
  // getAccessTokenSilently() calls (common on initial page load, when
  // several components mount at once) don't each trigger their own
  // refresh-token network request.
  const sessionPromiseRef = useRef(null);

  const refreshAuthState = useCallback(async () => {
    const session = await getCurrentSession();
    if (session) {
      setIsAuthenticated(true);
      setUser(buildUserFromClaims(session.claims));
    } else {
      setIsAuthenticated(false);
      setUser(null);
    }
    return session;
  }, []);

  useEffect(() => {
    refreshAuthState().finally(() => setIsLoading(false));
  }, [refreshAuthState]);

  // ── Auth0-compatible API — the 38 existing consumer files use these ──────

  const loginWithRedirect = useCallback(() => {
    // Auth0's loginWithRedirect() sent users to Auth0's own hosted Universal
    // Login page (which handled email/password AND social buttons in one
    // place). Cognito's SRP flow has no equivalent hosted page for plain
    // email/password, so this now navigates to our own in-app /login route
    // instead — which itself offers both the email/password form and the
    // Apple/Google buttons (those still redirect externally, to Cognito's
    // Hosted UI and then the provider's own sign-in screen).
    navigate("/login");
  }, [navigate]);

  const logout = useCallback(
    (options) => {
      cognitoSignOut();
      setIsAuthenticated(false);
      setUser(null);

      // Also clear the Hosted UI's own SSO session (relevant for anyone who
      // signed in via Apple/Google federation) by hitting Cognito's /logout
      // endpoint, same idea as Auth0's logout() redirecting to its
      // /v2/logout endpoint.
      // Cognito requires logout_uri to EXACTLY match a registered logout
      // URL, character for character — including the trailing slash.
      // Several call sites across the app pass
      // `returnTo: window.location.origin` (no trailing slash), a common
      // Auth0-tutorial convention that Auth0 itself tolerated but Cognito
      // does not. Normalizing here means those call sites don't need to
      // know about this Cognito-specific quirk.
      let returnTo =
        options?.logoutParams?.returnTo || getLogoutRedirectUri();
      if (returnTo.startsWith("http") && !returnTo.endsWith("/")) {
        returnTo += "/";
      }
      const params = new URLSearchParams({
        client_id: COGNITO_CONFIG.clientId,
        logout_uri: returnTo,
      });
      window.location.href = `https://${COGNITO_CONFIG.hostedUiDomain}/logout?${params.toString()}`;
    },
    []
  );

  const getAccessTokenSilently = useCallback(async () => {
    // Accepts (and ignores) an options object for call-site compatibility —
    // existing code calls this as
    // getAccessTokenSilently({ authorizationParams: { audience } }), and
    // Cognito access tokens don't use Auth0-style audiences, so there's
    // nothing to do with that argument here.
    //
    // IMPORTANT: this calls getCurrentSession() directly rather than
    // refreshAuthState() — it must NOT update React state (setUser/
    // setIsAuthenticated) on every call. This function runs on essentially
    // every authenticated API request (via useApi()), and setUser() always
    // creates a brand-new object reference — any component with `user` in
    // a useEffect dependency array would re-fire on every single API call,
    // which itself calls this function again, creating an infinite loop of
    // renders and requests. State sync (refreshAuthState) only needs to run
    // on mount and after explicit login/logout, not on every token fetch.
    if (!sessionPromiseRef.current) {
      sessionPromiseRef.current = getCurrentSession().finally(() => {
        sessionPromiseRef.current = null;
      });
    }
    const session = await sessionPromiseRef.current;
    if (!session) {
      throw new Error("No active session — user is not authenticated.");
    }
    return session.accessToken;
  }, []);

  // ── New methods, for the custom Login/Signup pages to call directly ─────
  // (Not part of Auth0's original API — additive, so nothing existing breaks.)

  const signInWithEmail = useCallback(
    async (email, password) => {
      const { signIn } = await import("../services/cognitoAuthService");
      await signIn(email, password);
      await refreshAuthState();
    },
    [refreshAuthState]
  );

  const signInWithApple = useCallback(() => {
    redirectToFederatedSignIn("SignInWithApple");
  }, []);

  const signInWithGoogle = useCallback(() => {
    redirectToFederatedSignIn("Google");
  }, []);

  /**
   * Compatibility shim for AuthCallbackListener (native app only), which
   * still calls this exact method name/signature the way it did for Auth0's
   * SDK. On native, Cognito's OAuth redirect arrives as a custom-scheme URL
   * (com.verainsight.nephrotrax://callback?code=...&state=...) delivered via
   * Capacitor's appUrlOpen event, not as a normal page navigation — so
   * unlike the web flow (AuthCallbackPage, which reads query params from
   * the current page), this parses code/state directly out of the given
   * URL string.
   */
  const handleRedirectCallback = useCallback(
    async (url) => {
      const parsed = new URL(url);
      const code = parsed.searchParams.get("code");
      const state = parsed.searchParams.get("state");
      if (!code) {
        throw new Error("No authorization code in native callback URL.");
      }
      const { handleOAuthCallback } = await import("../services/cognitoAuthService");
      await handleOAuthCallback(code, state);
      await refreshAuthState();
    },
    [refreshAuthState]
  );

  const value = {
    isLoading,
    isAuthenticated,
    user,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
    // additive:
    signInWithEmail,
    signInWithApple,
    signInWithGoogle,
    handleRedirectCallback,
    refreshAuthState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Named useAuth0 (not useAuth) so the 38 existing files that already call
 * `useAuth0()` only need their import's SOURCE PATH changed — from
 * "@auth0/auth0-react" to this file — with zero changes to their actual
 * code, since the hook name and returned shape are unchanged.
 */
export function useAuth0() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth0() must be used within an <AuthProvider>.");
  }
  return context;
}